import { z } from 'zod';
import { assertDefined } from '@/modules/assert-defined';
import { reportSchemaMismatch } from '@/modules/report-schema-mismatch';

// One parsed Server-Sent-Events frame: the `event:` name (SSE default
// "message" when absent) and the joined `data:` payload.
export type SseEvent = { event: string; data: string };

// Splits an accumulated SSE text buffer into completed frames (blocks
// separated by a blank line) plus the unfinished tail. The caller owns the
// buffer: append the next network chunk to `rest` and call again — a frame
// split across reads parses once it completes. Handles \r\n line endings;
// blocks without a `data:` line (comments, keep-alives) are dropped.
export function parseSseChunk(buffer: string): {
  events: SseEvent[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');
  const rest = blocks.pop();
  // Stryker disable next-line CallExpression: equivalent — never fires: split() returns at least one element, so pop() is always defined
  assertDefined(rest);
  const events: SseEvent[] = [];

  for (const block of blocks) {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
      else if (line.startsWith('data:'))
        dataLines.push(line.slice('data:'.length).trim());
    }
    if (dataLines.length > 0)
      events.push({ event, data: dataLines.join('\n') });
  }

  return { events, rest };
}

// Parses one frame's `data` as JSON through `schema`. JSON.parse runs inside
// the zod pipeline so a malformed frame reports as a ZodError exactly like a
// shape mismatch; either way the frame fails OPEN — reported under
// `source`/`endpoint`, then null for the caller to skip.
export function parseSseFrame<S extends z.ZodType>(
  schema: S,
  data: string,
  source: Parameters<typeof reportSchemaMismatch>[0],
  endpoint: string,
): z.output<S> | null {
  const parsed = z
    .string()
    .transform((s, ctx): unknown => {
      try {
        return JSON.parse(s);
      } catch {
        ctx.addIssue('malformed JSON frame');
        return z.NEVER;
      }
    })
    .pipe(schema)
    .safeParse(data);
  if (parsed.success) return parsed.data;
  reportSchemaMismatch(source, endpoint, parsed.error, data);
  return null;
}
